import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce/lib';
import { useTimesheetLabelSuggestionContext } from 'modules/timesheet-labels';
import { useLabelSuggestion } from 'modules/timesheet-labels/hooks';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { LabelSuggestionsInput, TimeSignalClassification } from 'graphql/types';
import { FetchSuggestionsHandlerProps } from 'modules/notes/addNote/hooks';
import { NOTE_FIELDS } from 'modules/notes/common';
import { SplitEntryFormData } from '../splitNoteSchema';
import { SplitEntry, SPLIT_NOTE_FIELDS } from '../types';
import { mapFormValuesToSuggestionsInput } from './useSplitFormChangeHandlers';

export interface SplitEntryFetchSuggestionsProps {
  fetchSuggestions(
    index: number
  ): (event: FetchSuggestionsHandlerProps) => void;
}

export const useSplitEntryFetchSuggestions = (
  index: number
): SplitEntryFetchSuggestionsProps => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const {
    getLabelSuggestions,
    labelSuggestionData,
    loading
  } = useLabelSuggestion();
  const { setLabelSuggestions } = useTimesheetLabelSuggestionContext();

  const { getValues, setValue } = useFormContext<{
    splitEntries: SplitEntryFormData[];
    classification: TimeSignalClassification;
  }>();

  const debounced = useDebouncedCallback((input?: LabelSuggestionsInput) => {
    getLabelSuggestions(input);
  }, 1000);

  useEffect(() => {
    if (loading) return;
    if (!labelSuggestionData) {
      setLabelSuggestions(undefined);
    } else {
      setLabelSuggestions(labelSuggestionData);
      setValue(
        `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${index}.${SPLIT_NOTE_FIELDS.LABEL_VAL_INF_ID}`,
        labelSuggestionData?.labelSuggestions?.inferenceId,
        { shouldDirty: false }
      );
    }
  }, [setLabelSuggestions, labelSuggestionData, loading, setValue, index]);

  const fetchSuggestions = useCallback(
    (index: number) => ({
      description,
      hasSuggestionStartTime,
      hasSuggestionEndTime,
      hasSuggestionDuration
    }: FetchSuggestionsHandlerProps) => {
      if (isSmartSuggestionEnabled) {
        const { splitEntries } = getValues();
        const values: SplitEntry = splitEntries[index];
        const labelSuggestionsInput = mapFormValuesToSuggestionsInput({
          ...values,
          description:
            description === undefined ? values.description : description,
          [NOTE_FIELDS.START_TIME]: hasSuggestionStartTime
            ? null
            : values[NOTE_FIELDS.START_TIME],
          [NOTE_FIELDS.END_TIME]: hasSuggestionEndTime
            ? null
            : values[NOTE_FIELDS.END_TIME],
          [NOTE_FIELDS.DURATION]: hasSuggestionDuration
            ? undefined
            : values[NOTE_FIELDS.DURATION]
        });
        debounced(labelSuggestionsInput);
      }
    },
    [isSmartSuggestionEnabled, getValues, debounced]
  );
  return { fetchSuggestions };
};

export default useSplitEntryFetchSuggestions;
